/*
 * POOM — animacje wejscia przy przewijaniu (CSS transitions + IntersectionObserver, bez bibliotek).
 *
 * Wczesniej GSAP (72 KB + ~0,3-0,5 s procesora na telefonie) — wjazdy to tylko opacity/transform,
 * wiec robi je CSS: klasa .pk-m ukrywa (opacity 0 + przesuniecie), .pk-m--in odslania z przejsciem,
 * stagger = transition-delay liczony per element. Style w assets/poom.css.
 *
 * Zasady (wydajnosc + SEO):
 *  - ukrywamy i animujemy TYLKO elementy ponizej pierwszego ekranu w chwili startu; to, co widac od razu
 *    (hero, LCP), zostaje nietkniete — zero mrugniecia i zero opoznienia LCP,
 *  - tylko transform + opacity (kompozytor); po animacji klasy znikaja (zero sladu w stylach),
 *  - prefers-reduced-motion => nic nie robimy; skrypt nie laduje sie w edytorze motywu (snippet pk-head),
 *  - sekcje dociagniete przez pk-lazy-sections.js (zdarzenie pk:section-loaded) tez dostaja animacje.
 */
(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // Grupy elementow: sel = co animujemy, y/scale = stan startowy, stagger = odstep (s) miedzy elementami
  // wchodzacymi w tej samej klatce.
  const GROUPS = [
    { sel: '.resource-list__item', y: 40, scale: 0.97, stagger: 0.08 }, // karty produktow i kategorii
    { sel: '.gal-item', y: 28, stagger: 0.06 },                        // Galeria (galeria-zdjec)
    { sel: '.ig-card', y: 28, stagger: 0.08 },                         // Instagram UGC
    { sel: '.pkn__hero, .pkn__item', y: 24, stagger: 0.08 },           // baner "Nowosci" (custom liquid)
    { sel: '.blog-post-card, .resource-card', y: 28, stagger: 0.08 },
    { sel: '.section-resource-list__header, .gal-head, .ig-head', y: 18, stagger: 0 },
    { sel: '.email-signup__form, .icon-block', y: 18, stagger: 0.06 },
    { sel: '.pk-promo__head, .pk-tile', y: 26, stagger: 0.12 }, // Aktywne promocje (kafelki)
    { sel: '.pk-faq__head', y: 18, stagger: 0 },
    { sel: '.pk-faq__item', y: 16, stagger: 0.06 },
    { sel: '.jdgm-carousel-wrapper, .jdgm-all-reviews-rating-wrapper', y: 24, stagger: 0 },
    { sel: '.pk-rev__logo, .pk-rev__title, .pk-rev__sub, .pk-rev__summary', y: 18, stagger: 0.08 }, // Opinie klientow: naglowek
    { sel: '.pk-rev__row', y: 36, stagger: 0.15 },                      // Opinie: rzedy kart (karty dochodza w miejsce szkieletu)
  ];
  const DURATION = 900; // ms, jak w poom.css (.pk-m--in)

  const seen = new WeakSet();
  const pending = new Map(); // grupa -> elementy wchodzace w tej samej klatce
  let flushQueued = false;

  const finish = (el) => {
    el.classList.remove('pk-m', 'pk-m--in');
    el.style.removeProperty('--pk-m-y');
    el.style.removeProperty('--pk-m-scale');
    el.style.removeProperty('--pk-m-delay');
  };

  const flush = () => {
    flushQueued = false;
    for (const [group, els] of pending) {
      // kolejnosc wejscia = polozenie na ekranie (lewo-prawo, gora-dol); same odczyty, potem zapisy
      const rects = els.map((el) => [el, el.getBoundingClientRect()]);
      rects.sort(([, a], [, b]) => a.top - b.top || a.left - b.left);
      rects.forEach(([el], i) => {
        const delay = i * group.stagger;
        if (delay) el.style.setProperty('--pk-m-delay', `${delay}s`);
        el.classList.add('pk-m--in');
        setTimeout(() => finish(el), DURATION + delay * 1000 + 50);
      });
    }
    pending.clear();
  };

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        io.unobserve(entry.target);
        const group = entry.target.__pkGroup;
        if (!pending.has(group)) pending.set(group, []);
        pending.get(group).push(entry.target);
      }
      if (pending.size && !flushQueued) {
        flushQueued = true;
        requestAnimationFrame(flush);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.01 }
  );

  const prepare = (root, { all = false } = {}) => {
    const fold = window.innerHeight;
    const candidates = [];
    for (const group of GROUPS) {
      root.querySelectorAll(group.sel).forEach((el) => {
        if (seen.has(el) || el.closest('[data-pk-no-motion], dialog, .header, header-component, predictive-search-component, .predictive-search')) return;
        seen.add(el);
        candidates.push([el, group]);
      });
    }
    // element wewnatrz innego animowanego (np. h2 w naglowku sekcji) dostalby podwojne przesuniecie
    const set = new Set(candidates.map(([el]) => el));
    const nested = (el) => { for (let p = el.parentElement; p; p = p.parentElement) if (set.has(p)) return true; return false; };
    // najpierw same odczyty geometrii (jeden layout), potem zapisy
    const vw = window.innerWidth;
    const below = candidates.filter(([el]) => {
      if (nested(el)) return false;
      const r = el.getBoundingClientRect();
      // elementy schowane w bok w poziomej karuzeli (telefon) zostawiamy widoczne — inaczej
      // "wylanialyby sie" przy przesuwaniu palcem, bo nie przecinaja ekranu przy przewijaniu w pionie
      return r.height > 0 && (all || r.top > fold) && r.left < vw - 1 && r.right > 1;
    });
    for (const [el, group] of below) {
      el.__pkGroup = group;
      el.style.setProperty('--pk-m-y', `${group.y}px`);
      if (group.scale) el.style.setProperty('--pk-m-scale', String(group.scale));
      el.classList.add('pk-m');
      io.observe(el);
    }
  };

  const start = () => prepare(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();

  // sekcja dociagnieta (pk-lazy-sections.js): animujemy wszystkie karty, takze te juz na ekranie —
  // w ich miejscu byl szkielet, wiec wjazd zastepuje mrugniecie "szkielet -> tresc"
  document.addEventListener('pk:section-loaded', (e) => requestAnimationFrame(() => prepare(e.detail.section, { all: true })));

  /* Delikatna paralaksa tla hero — tylko desktop, tylko transform, wylaczana gdy hero poza ekranem. */
  const hero = document.querySelector('#MainContent .shopify-section:first-child :is(.hero__media-wrapper, .hero__media)');
  if (hero && window.matchMedia('(min-width: 990px)').matches && document.documentElement.dataset.pkParallax === '1') {
    const media = hero.querySelector('img, video') || hero;
    let visible = true;
    let ticking = false;
    new IntersectionObserver(([e]) => { visible = e.isIntersecting; }).observe(hero);
    media.style.transformOrigin = '50% 40%';
    media.style.willChange = 'transform';
    const update = () => {
      ticking = false;
      if (!visible) return;
      const y = Math.min(window.scrollY, window.innerHeight) * 0.18;
      media.style.transform = `translate3d(0, ${y}px, 0) scale(1.08)`;
    };
    update();
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
  }
})();
